import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type JSX } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  shortDate,
  type AuditEvent,
  type Finding,
  type Issue,
  type Me,
  type RiskTypeCatalogueEntry,
  type Vendor,
  readIf,
} from '../lib/api'
import { statusWord } from '../lib/status'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from '../components/DataTable'
import {
  FINDING_SEVERITIES,
  FindingsTable,
  findingSourceWord,
  findingStanding,
  mergedFinding,
} from '../components/VendorFindings'
import { findingBody } from '../components/footers'
import { Dialog, Status, Toggletip, type Tone } from '../components/kit'
import { AssessTabs } from '../components/assess/AssessTabs'
import { afterEdit, fromServer, type Listed } from '../lib/issues'
import { useSave } from '../lib/save'
import { VendorChoice } from '../components/VendorChoice'
import { personLabel, usePeopleNames } from '../lib/people'
import { checkFindingDue, todayIso } from '../lib/dateRules'
import '../styles/prose.css'

// The remediation register: every finding in the tenant, across vendors.
//
// WHY THIS SCREEN EXISTS SEPARATELY FROM THE VENDOR RECORD
//
// Remediation is worked by owner and by date, not by vendor. The person who has
// eleven findings due this month has them spread over nine suppliers, and a
// screen that makes them open nine records to see that is a screen that gets
// replaced by a spreadsheet nobody else can read.
//
// WHY THE WHOLE SET IS READ AT ONCE AND NARROWED HERE
//
// The counts along the top have to agree with each other and with the rows
// underneath. Asking the API once per filter chip would mean the strip counted
// one set and the table showed another, and the ageing that decides "overdue"
// is the API's arithmetic either way: it arrives on the row.
//
// WHY A ROW OPENS A DRAWER
//
// Each row used to carry its owner box, date box, severity override and every
// status move with the sentence explaining each, which made a register of sixty
// findings a wall nobody could scan. The row now says severity, what, whose,
// when and where it stands; the drawer holds the detail and the same editing
// cells the vendor record uses, so every write goes through the one
// implementation of the transition rules rather than a second copy here.

// The largest page the API will serve. Asking for it means the counts above the
// table describe the whole register on every estate small enough to fit, and
// the screen says so plainly on the ones that are not.
const PAGE_LIMIT = 500

type View = 'OWED' | 'OVERDUE' | 'MINE' | 'ACCEPTED' | 'CLOSED' | 'ALL'

const VIEWS: { key: View; label: string }[] = [
  { key: 'OWED', label: 'Still owed' },
  { key: 'OVERDUE', label: 'Overdue' },
  { key: 'MINE', label: 'Mine' },
  { key: 'ACCEPTED', label: 'Risk accepted' },
  { key: 'CLOSED', label: 'Closed' },
  { key: 'ALL', label: 'All' },
]

const SEVERITY_RANK: Record<string, number> = { LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 }

function words(value: string): string {
  return value.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
}

// A toggletip holds 200 characters. The API's own sentences can be longer; they
// are cut at a word here and shown whole where there is room.
function tip(text: string): string {
  const t = text.trim()
  if (t.length <= 200) return t
  const cut = t.slice(0, 196)
  const space = cut.lastIndexOf(' ')
  return (space > 140 ? cut.slice(0, space) : cut) + '...'
}

// Severity as a word, a colour and a shape, so it reads without the colour:
// the same four shapes the band chip uses, from circle to hexagon.
function SeverityMark({ severity }: { severity: string }): JSX.Element {
  const key = severity.toUpperCase()
  const common = { fill: 'currentColor', stroke: 'currentColor', strokeWidth: 1.4, strokeLinejoin: 'round' as const }
  return (
    <span className={'prose-sev prose-sev-' + key.toLowerCase()}>
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
        {key === 'LOW' && <circle cx="6" cy="6" r="4.4" {...common} />}
        {key === 'MEDIUM' && <path d="M6 1.2 10.8 6 6 10.8 1.2 6Z" {...common} />}
        {key === 'HIGH' && <path d="M6 1.1 11.2 10.6H0.8Z" {...common} />}
        {key === 'CRITICAL' && <path d="M3.6 1.2h4.8L11 6l-2.6 4.8H3.6L1 6Z" {...common} />}
      </svg>
      {words(key)}
    </span>
  )
}

// The state in the vocabulary's words (lib/status.ts).
function statusOf(f: Finding): { text: string; tone: Tone } {
  const s = statusWord('finding', f.status)
  return { text: s.word, tone: s.tone }
}

// Where the due date stands, only while the finding is still owed: the clock
// stops when it is closed or accepted.
function ageingOf(f: Finding): { text: string; tone: Tone } | null {
  if (!findingStanding(f.status)) return null
  const d = f.ageingDays
  if (d > 0) return { text: d === 1 ? '1 day overdue' : d + ' days overdue', tone: 'crit' }
  if (d === 0) return { text: 'Due today', tone: 'warn' }
  const left = Math.abs(d)
  return { text: left === 1 ? '1 day left' : left + ' days left', tone: left <= 7 ? 'warn' : 'plain' }
}

type Draft = {
  vendorId: string
  title: string
  detail: string
  citation: string
  severity: string
  dueOn: string
  ownerUserId: string
  riskType: string
}

const EMPTY_DRAFT: Draft = {
  vendorId: '',
  title: '',
  detail: '',
  citation: '',
  severity: 'MEDIUM',
  dueOn: '',
  ownerUserId: '',
  riskType: '',
}

// The field names match the API's own issue fields, so a check made here and a
// 422 from the server land under the same control with the same wording.
function checkDraft(d: Draft): Issue[] {
  const out: Issue[] = []
  if (!d.vendorId) {
    out.push({ field: 'vendorId', code: 'REQUIRED', detail: 'A finding is always about a vendor.' })
  }
  if (!d.title.trim()) {
    out.push({ field: 'title', code: 'REQUIRED', detail: 'Name what has to be fixed, in words the owner will recognise.' })
  }
  if (!d.severity) {
    out.push({ field: 'severity', code: 'REQUIRED', detail: 'Choose a severity. It sets the default due date.' })
  }
  // D18 of the date rules: a finding is not raised already overdue.
  const due = checkFindingDue(d.dueOn, todayIso())
  if (due) out.push({ field: 'dueOn', code: 'IN_THE_PAST', detail: due })
  return out
}

const ANCHOR: Record<string, string> = {
  vendorId: 'fnd-vendor',
  title: 'fnd-title',
  detail: 'fnd-detail',
  citation: 'fnd-citation',
  severity: 'fnd-severity',
  dueOn: 'fnd-due',
  ownerUserId: 'fnd-owner',
  riskType: 'fnd-risk-type',
}

// The console routes on the fragment, so an in-page link written as href="#id"
// would change the route rather than move to the field. Focus is moved by hand
// and the fragment left alone.
function focusField(id: string): void {
  const el = document.getElementById(id)
  if (!el) return
  el.scrollIntoView({ block: 'center' })
  el.focus()
}

// The act on a finding's history, in the words the buttons use. A reopening
// from In progress is the Put back in the queue button; from anywhere else it
// is a reopening.
function historyWord(e: AuditEvent): string {
  const p = (e.payload ?? {}) as { from?: unknown }
  switch (e.action) {
    case 'finding.raised':
      return 'Raised'
    case 'finding.started':
      return 'Work started'
    case 'finding.resolved':
      return 'Resolved'
    case 'finding.withdrawn':
      return 'Withdrawn'
    case 'finding.accepted':
      return 'Accepted as a risk'
    case 'finding.reopened':
      return p.from === 'IN_PROGRESS' ? 'Put back in the queue' : 'Reopened'
    case 'finding.updated':
      return 'Updated'
    case 'finding.escalated':
      return 'Escalated'
    default:
      return words(e.action.replace(/^finding\./, ''))
  }
}

// The reason a person gave, or the one the system recorded, off the payload the
// chain hashed. Nothing is composed here.
function historyReason(e: AuditEvent): string {
  const p = (e.payload ?? {}) as { note?: unknown; reason?: unknown }
  if (typeof p.note === 'string' && p.note.trim()) return p.note.trim()
  if (typeof p.reason === 'string' && p.reason.trim()) return p.reason.trim()
  return ''
}

// The finding's own acts, newest first, read off the audit trail. This is
// where who raised it, and why it was resolved, withdrawn or put back, is
// kept, instead of on the register's rows. Read again whenever the row
// changes, so a move made in the drawer shows here with its reason.
function FindingHistory({ findingId, changedAt }: { findingId: string; changedAt: string }): JSX.Element | null {
  const [events, setEvents] = useState<AuditEvent[] | null>(null)
  const [failed, setFailed] = useState(false)
  const names = usePeopleNames()
  const may = can('audit.read')

  useEffect(() => {
    if (!may) return
    let live = true
    setFailed(false)
    api
      .audit({ entityType: 'finding', entityId: findingId, limit: 20 })
      .then((page) => {
        if (live) setEvents(page.events)
      })
      .catch(() => {
        if (live) setFailed(true)
      })
    return () => {
      live = false
    }
  }, [findingId, changedAt, may])

  if (!may) return null
  return (
    <div>
      <h3>History</h3>
      {failed ? (
        <p className="prose-sub">The history could not be read.</p>
      ) : events === null ? (
        <p className="prose-sub">Reading the history</p>
      ) : events.length === 0 ? (
        <p className="prose-sub">Nothing recorded yet.</p>
      ) : (
        <ol className="prose-history">
          {events.map((e) => {
            const reason = historyReason(e)
            return (
              <li key={e.eventId}>
                <span>
                  <b>{historyWord(e)}</b>
                  <span className="prose-sub">
                    {' '}
                    {personLabel(names, e.actorId) || 'not recorded'}, {shortDate(e.occurredAt)}
                  </span>
                </span>
                {reason && <p>{reason}</p>}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

export function Findings(): JSX.Element {
  const [rows, setRows] = useState<Finding[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<unknown>(null)
  const [me, setMe] = useState<Me | null>(null)
  const names = usePeopleNames()

  const [view, setView] = useState<View>('OWED')
  const [severities, setSeverities] = useState<string[]>([])
  // Rows somebody has just acted on. Resolving a finding moves it out of the
  // default filter, and a row that vanishes at the moment it is changed takes
  // its own confirmation with it, which is how somebody ends up pressing the
  // button a second time. They stay until the filter is touched again.
  const [held, setHeld] = useState<string[]>([])
  const [openId, setOpenId] = useState<string | null>(null)

  const [formOpen, setFormOpen] = useState(false)
  const save = useSave(formOpen, 'Record a finding')
  const submitting = save.busy
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [issues, setIssues] = useState<Listed[]>([])
  const [submitError, setSubmitError] = useState<ApiError | null>(null)
  const [raised, setRaised] = useState('')
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [riskTypes, setRiskTypes] = useState<RiskTypeCatalogueEntry[]>([])
  const summaryRef = useRef<HTMLDivElement>(null)
  const [focusSummary, setFocusSummary] = useState(false)

  const load = useCallback(() => {
    api
      .findings({ limit: PAGE_LIMIT })
      .then((page) => {
        setRows(page.findings)
        setTotal(page.total)
        setHeld([])
        setError(null)
      })
      .catch((err) => {
        setRows(null)
        setError(err)
      })
  }, [])

  useEffect(() => {
    load()
    api
      .me()
      .then(setMe)
      .catch(() => setMe(null))
    api
      .vendors({})
      .then((p) => setVendors(p.vendors))
      .catch(() => setVendors([]))
    readIf('config.read', () => api.riskTypes(), { riskTypes: [], note: '' })
      .then((r) => setRiskTypes(r.riskTypes))
      .catch(() => setRiskTypes([]))
  }, [load])

  // Focus lands on the summary only after React has flushed it into the DOM.
  // Calling focus() straight after setIssues does nothing: the element is still
  // hidden at that point and focus falls back to the body.
  useEffect(() => {
    if (!focusSummary) return
    summaryRef.current?.focus()
    setFocusSummary(false)
  }, [focusSummary])

  const inView = useCallback(
    (f: Finding, v: View): boolean => {
      switch (v) {
        case 'OWED':
          return findingStanding(f.status)
        case 'OVERDUE':
          return findingStanding(f.status) && f.ageingDays > 0
        case 'MINE':
          return me != null && f.ownerUserId === me.subject
        case 'ACCEPTED':
          return f.status === 'ACCEPTED'
        case 'CLOSED':
          return f.status === 'RESOLVED' || f.status === 'WITHDRAWN'
        case 'ALL':
          return true
      }
    },
    [me],
  )

  const counts = useMemo(() => {
    const all = rows ?? []
    const owed = all.filter((f) => findingStanding(f.status))
    const byView = {} as Record<View, number>
    for (const v of VIEWS) byView[v.key] = all.filter((f) => inView(f, v.key)).length
    const bySeverity: Record<string, number> = {}
    for (const s of FINDING_SEVERITIES) bySeverity[s] = all.filter((f) => f.severity === s && inView(f, view)).length
    return {
      total: all.length,
      owed: owed.length,
      overdue: owed.filter((f) => f.ageingDays > 0).length,
      critical: owed.filter((f) => f.severity === 'CRITICAL').length,
      stopped: owed.filter((f) => !f.stillReproducing).length,
      byView,
      bySeverity,
    }
  }, [rows, inView, view])

  const matches = useCallback(
    (f: Finding): boolean => {
      if (severities.length > 0 && !severities.includes(f.severity)) return false
      return inView(f, view)
    },
    [view, severities, inView],
  )

  const shown = useMemo(
    () => (rows ?? []).filter((f) => matches(f) || held.includes(f.id)),
    [rows, matches, held],
  )

  const kept = useMemo(() => shown.filter((f) => !matches(f)).length, [shown, matches])

  const vendorOptions = useMemo(
    () => [...vendors].sort((a, b) => (a.displayName || a.legalName).localeCompare(b.displayName || b.legalName, 'en')),
    [vendors],
  )

  const replace = useCallback((fresh: Finding) => {
    setRows((current) => (current ?? []).map((f) => (f.id === fresh.id ? mergedFinding(f, fresh) : f)))
    setHeld((current) => (current.includes(fresh.id) ? current : [...current, fresh.id]))
  }, [])

  function choose(next: View, severity?: string) {
    setView(next)
    setSeverities(severity ? [severity] : [])
    setHeld([])
  }

  // An edit, and the problems that are still problems after it.
  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    const next = { ...draft, [key]: value }
    setDraft(next)
    setIssues((list) => afterEdit(list, key, checkDraft(next)))
  }

  function issueFor(field: string): Issue | undefined {
    return issues.find((i) => i.field === field)
  }

  // The date rule is said when the person leaves the field, not only on Save.
  function leaveDue() {
    const msg = checkFindingDue(draft.dueOn, todayIso())
    setIssues((cur) => {
      const rest = cur.filter((i) => i.field !== 'dueOn')
      return msg ? [...rest, { field: 'dueOn', code: 'IN_THE_PAST', detail: msg }] : rest
    })
  }

  function toggleSeverity(s: string) {
    setSeverities((current) => (current.includes(s) ? current.filter((x) => x !== s) : [...current, s]))
    setHeld([])
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setRaised('')
    setSubmitError(null)
    const found = checkDraft(draft)
    setIssues(found)
    if (found.length > 0) {
      // Focus moves to the summary only after a failed submit, the one moment a
      // reader has asked for the verdict. Never while somebody is still typing.
      setFocusSummary(true)
      return
    }
    save.send(api.raiseFinding(draft.vendorId, findingBody(draft)), {
      done: (reply) => {
        setDraft(EMPTY_DRAFT)
        setIssues([])
        setFormOpen(false)
        // The API's own sentence: it names the due date it worked out from the
        // severity and the owner it resolved off the vendor, neither of which
        // this form sent.
        setRaised(reply.note)
        load()
      },
      failed: (err) => {
        const failure = err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
        if (failure.status === 422 && failure.issues.length > 0) {
          setIssues(fromServer(failure.issues))
          setFocusSummary(true)
        } else {
          setSubmitError(failure)
        }
      },
    })
  }


  const mayRaise = can('finding.manage')
  const open = openId ? (rows ?? []).find((f) => f.id === openId) ?? null : null
  // Held here and not by the cell that draws the moves: the cell goes when the
  // drawer closes, and a move still out must keep that finding's actions
  // locked when it is opened again.
  const rowSave = useSave(open ? open.id : null, open?.title ?? 'the finding')

  const columns: Column<Finding>[] = [
    {
      key: 'severity',
      header: 'Severity',
      sortValue: (f) => SEVERITY_RANK[f.severity] ?? 0,
      render: (f) => <SeverityMark severity={f.severity} />,
    },
    {
      key: 'finding',
      header: 'Finding',
      sortValue: (f) => f.title,
      render: (f) => (
        <>
          <button type="button" className="prose-rowlink prose-clamp" title={f.title} onClick={() => setOpenId(f.id)}>
            {f.title}
          </button>
          {/* Where it came from and who raised it are in the drawer, not on
              the row: the row is what is owed, by whom, by when. */}
          {!f.stillReproducing && findingStanding(f.status) && (
            <span className="prose-sub prose-chips">
              <Status tone="info">Not reproducing</Status>
            </span>
          )}
        </>
      ),
    },
    {
      key: 'vendor',
      header: 'Vendor',
      sortValue: (f) => f.vendorName ?? f.vendorCode ?? f.vendorId,
      render: (f) => (
        <>
          <a href={'#/vendor/' + encodeURIComponent(f.vendorId)}>{f.vendorName || 'Open the vendor'}</a>
          {f.vendorCode && <span className="prose-sub prose-mono">{f.vendorCode}</span>}
        </>
      ),
    },
    {
      key: 'owner',
      header: 'Owner',
      sortValue: (f) => personLabel(names, f.ownerUserId),
      render: (f) => <span>{personLabel(names, f.ownerUserId) || 'Not recorded'}</span>,
    },
    {
      // Sorted on the ageing rather than the date, so descending is most
      // overdue first, which is the way this column is read.
      key: 'due',
      header: 'Due',
      sortValue: (f) => f.ageingDays,
      render: (f) => {
        const age = ageingOf(f)
        return (
          <>
            <span className="prose-mono prose-nowrap">{f.dueOn ? shortDate(f.dueOn) : 'No date'}</span>
            {age && (
              <span className="prose-sub">
                <Status tone={age.tone}>{age.text}</Status>
              </span>
            )}
          </>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (f) => ['OPEN', 'IN_PROGRESS', 'ACCEPTED', 'RESOLVED', 'WITHDRAWN'].indexOf(f.status),
      render: (f) => {
        const s = statusOf(f)
        return <Status tone={s.tone}>{s.text}</Status>
      },
    },
  ]

  const tiles: { key: string; n: number; label: string; hot: boolean; pressed: boolean; pick: () => void }[] = [
    {
      key: 'owed',
      n: counts.owed,
      label: 'Still owed',
      hot: false,
      pressed: view === 'OWED' && severities.length === 0,
      pick: () => choose('OWED'),
    },
    {
      key: 'overdue',
      n: counts.overdue,
      label: 'Overdue',
      hot: counts.overdue > 0,
      pressed: view === 'OVERDUE' && severities.length === 0,
      pick: () => choose('OVERDUE'),
    },
    {
      key: 'critical',
      n: counts.critical,
      label: 'Critical, still owed',
      hot: counts.critical > 0,
      pressed: view === 'OWED' && severities.length === 1 && severities[0] === 'CRITICAL',
      pick: () => choose('OWED', 'CRITICAL'),
    },
  ]

  const openStatus = open ? statusOf(open) : null
  const openAge = open ? ageingOf(open) : null

  return (
    <div className="prose-page">
      <AssessTabs at="findings" />
      <div className="prose-head">
        <div className="prose-head-main">
          {/* The count owed is the first tile below, so the heading does not
              say it a second time, and the one line about findings sits beside
              it as a hover rather than on a line of its own. */}
          <div className="prose-title-row">
            <h1>Findings</h1>
            <Toggletip label="About findings">
              Raised by a rule or an answer on a score, by the website check, or by hand. Each has an owner and a due
              date until it is resolved, withdrawn or accepted.
            </Toggletip>
          </div>
        </div>
        <div className="prose-actions">
          {/* Open to everyone, as the inline form was: the submit inside is
              what a reader without the capability is refused. */}
          <button
            type="button"
            className="primary"
            onClick={() => {
              setSubmitError(null)
              setFormOpen(true)
            }}
          >
            Record a finding
          </button>
          {!mayRaise && <Toggletip label="Why you cannot record one">{tip(refusalReason('finding.manage'))}</Toggletip>}
        </div>
      </div>

      {error != null && <ErrorBox error={error} what="The findings register" />}

      {raised && (
        <p className="prose-line" role="status">
          <Status tone="ok">Recorded</Status>
          The new finding is on the register
          <Toggletip label="What was recorded">{tip(raised)}</Toggletip>
        </p>
      )}

      {rows && (
        <div className="prose-tiles" role="group" aria-label="Quick filters">
          {tiles.map((t) => (
            <button
              key={t.key}
              type="button"
              className={'prose-tile' + (t.hot ? ' prose-hot' : '')}
              aria-pressed={t.pressed}
              aria-label={t.label + ': ' + t.n}
              onClick={t.pick}
            >
              <span className="prose-tile-n">{t.n}</span>
              <span className="prose-tile-l">{t.label}</span>
            </button>
          ))}
        </div>
      )}

      {rows && counts.stopped > 0 && (
        <p className="prose-line">
          <Status tone="info">{counts.stopped} not reproducing</Status>
          <Toggletip label="Why they stay open">
            The rule no longer fires on the last score. That is not evidence the gap was closed, so each still needs
            closing with a reason.
          </Toggletip>
        </p>
      )}

      <section className="panel panel-flush" aria-labelledby="register-heading">
        <div className="prose-panelhead">
          <h2 id="register-heading">Register</h2>
          <span className="prose-grow" />
          <span className="prose-seg" role="group" aria-label="Show">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                type="button"
                className="prose-seg-btn"
                aria-pressed={view === v.key}
                disabled={v.key === 'MINE' && me === null}
                title={v.key === 'MINE' && me === null ? 'Who you are signed in as could not be read.' : undefined}
                onClick={() => {
                  setView(v.key)
                  setHeld([])
                }}
              >
                {v.label}
                {rows && <b>{counts.byView[v.key]}</b>}
              </button>
            ))}
          </span>
          <span className="prose-seg" role="group" aria-label="Severity">
            {FINDING_SEVERITIES.map((s) => (
              <button
                key={s}
                type="button"
                className="prose-seg-btn"
                aria-pressed={severities.includes(s)}
                onClick={() => toggleSeverity(s)}
              >
                {words(s)}
                {rows && <b>{counts.bySeverity[s]}</b>}
              </button>
            ))}
          </span>
          {severities.length > 0 && (
            <button
              type="button"
              className="prose-link"
              onClick={() => {
                setSeverities([])
                setHeld([])
              }}
            >
              Clear severity
            </button>
          )}
        </div>

        {!rows && error == null && <Loading what="the findings register" />}

        {rows && kept > 0 && (
          <p className="prose-empty">
            <Status tone="neutral">{kept} held</Status>
            {kept === 1 ? 'A changed row no longer matches this filter' : 'Changed rows no longer match this filter'}
            <Toggletip label="Why rows are held">
              Kept on screen so you can see what your change did. They go on the next read.
            </Toggletip>
          </p>
        )}

        {rows && (
          <DataTable
            caption="Findings, still owed first and most overdue first among those"
            columns={columns}
            rows={shown}
            // A row the reader has just changed or recorded is held on screen
            // so they see what their change did. Under a column sort it can
            // land below the tenth row, so while anything is held every row
            // is listed.
            firstRows={held.length === 0 ? 10 : undefined}
            rowKey={(f) => f.id}
            empty={{
              title: counts.total === 0 ? 'No finding has been raised.' : 'No finding matches this filter.',
              detail:
                counts.total === 0
                  ? 'Scoring an assessment raises one for each gate that fired.'
                  : 'Choose All to see every one on the register.',
            }}
            footer={
              <ListCount shown={shown.length} total={counts.total} noun="findings">
                {total > rows.length && (
                  <>
                    , {rows.length} of {total} findings read{' '}
                    <Toggletip label="Why the counts are partial">
                      The counts describe this page of the register, not the whole of it. Narrow with the filters.
                    </Toggletip>
                  </>
                )}
              </ListCount>
            }
          />
        )}
      </section>

      <Dialog shape="drawer" kind="record" open={open !== null} onClose={() => setOpenId(null)} title={open?.title ?? 'Finding'}>
        {open && (
          <div className="prose-drawer-stack">
            <div className="prose-chips">
              <SeverityMark severity={open.severity} />
              {openStatus && <Status tone={openStatus.tone}>{openStatus.text}</Status>}
              {openAge && <Status tone={openAge.tone}>{openAge.text}</Status>}
              {!open.stillReproducing && findingStanding(open.status) && (
                <>
                  <Status tone="info">Not reproducing</Status>
                  <Toggletip label="What not reproducing means">
                    The rule did not fire on the last score. That is not evidence the underlying thing was fixed, so it
                    waits for somebody to close it and say why.
                  </Toggletip>
                </>
              )}
            </div>

            <dl className="prose-kv">
              <dt>Vendor</dt>
              <dd>
                <a href={'#/vendor/' + encodeURIComponent(open.vendorId)}>{open.vendorName || 'Open the vendor'}</a>
                {open.vendorCode && <span className="prose-sub prose-mono"> {open.vendorCode}</span>}
              </dd>
              {/* Source and who raised it live here, not on the register's
                  rows. Every source was once shown as by hand except a rule's;
                  an answer and a website check are neither. */}
              <dt>Source</dt>
              <dd>
                {findingSourceWord(open.source)}
                {open.source === 'GATE' && open.gateKey && <span className="prose-mono"> {open.gateKey}</span>}
              </dd>
              {/* A person where one is recorded; a rule that fired on a score
                  often carries none, and "not recorded" beside a date reads
                  as a gap rather than as what happened. */}
              {open.raisedBy && (
                <>
                  <dt>Raised by</dt>
                  <dd>{personLabel(names, open.raisedBy)}</dd>
                </>
              )}
              {open.raisedAt && (
                <>
                  <dt>Raised on</dt>
                  <dd>{shortDate(open.raisedAt)}</dd>
                </>
              )}
              {open.assessmentId && (open.source === 'GATE' || open.source === 'ANSWER') && (
                <>
                  <dt>Last seen on</dt>
                  <dd>
                    <a href={'#/assessment/' + encodeURIComponent(open.assessmentId)}>The assessment that scored it</a>
                  </dd>
                </>
              )}
              {open.riskType && (
                <>
                  <dt>Risk type</dt>
                  <dd>{open.riskTypeName || words(open.riskType)}</dd>
                </>
              )}
              {open.citation && (
                <>
                  <dt>Citation</dt>
                  <dd>
                    <span className="prose-cite" title={open.citation}>
                      {open.citation}
                    </span>
                  </dd>
                </>
              )}
              {open.severity !== open.derivedSeverity && (
                <>
                  <dt>{open.source === 'MANUAL' ? 'Raised as' : 'Derived'}</dt>
                  <dd>
                    {words(open.derivedSeverity)}
                    {open.severityReason ? ': ' + open.severityReason : ''}
                  </dd>
                </>
              )}
            </dl>

            {open.detail && (
              <div>
                <h3>What this says</h3>
                <p className="prose-quote">{open.detail}</p>
              </div>
            )}

            <div>
              <h3>Work on it</h3>
              <div className="prose-fedit">
                <FindingsTable
                  caption={'Owner, due date, severity and status of ' + open.title}
                  rows={[open]}
                  showVendor={false}
                  onChanged={replace}
                  save={rowSave}
                  empty={{ title: 'This finding is no longer on the register.', detail: 'Reload the register.' }}
                />
              </div>
            </div>

            <FindingHistory findingId={open.id} changedAt={open.updatedAt + open.status} />
          </div>
        )}
      </Dialog>

      <Dialog
        shape="drawer"
        kind="form"
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title="Record a finding"
        busy={submitting}
        problems={issues.length}
        primary={{
          label: submitting ? 'Recording' : 'Record it',
          form: 'raise-finding-form',
          off: !mayRaise && refusalReason('finding.manage'),
          actionId: 'tprm.findings.a01',
        }}
        more={
          <button
            type="button"
            onClick={() => {
              setDraft(EMPTY_DRAFT)
              setIssues([])
              setSubmitError(null)
            }}
          >
            Clear
          </button>
        }
        // Said in the open, beside the button: it was behind an (i), and it is
        // the one thing about recording that cannot be taken back.
        note="A finding recorded by hand is never deleted. One raised in error is withdrawn, with the reason on the record."
      >
        <form id="raise-finding-form" onSubmit={submit} noValidate className="prose-drawer-stack">
          <div
            className="form-errors"
            ref={summaryRef}
            tabIndex={-1}
            role={issues.length > 0 ? 'alert' : undefined}
            hidden={issues.length === 0}
          >
            <h3>
              {issues.length} thing{issues.length === 1 ? '' : 's'} to fix before this can be recorded
            </h3>
            <ol>
              {issues.map((i) => (
                <li key={i.field + i.code}>
                  <button type="button" className="linkish" onClick={() => focusField(ANCHOR[i.field] ?? 'fnd-vendor')}>
                    {i.detail}
                  </button>
                </li>
              ))}
            </ol>
          </div>

          {/* Not ErrorBox: this was an act that was attempted and refused, not
              a panel that failed to load. */}
          {submitError && (
            <div className="error-box" role="alert">
              <h3>The finding was not recorded ({submitError.code})</h3>
              <p>{submitError.message}</p>
              <p className="muted">{submitError.remedy}</p>
            </div>
          )}

          <div className="field">
            <label htmlFor="fnd-vendor">Vendor</label>
            <VendorChoice
              bare
              browse
              id="fnd-vendor"
              label="Vendor"
              value={draft.vendorId}
              options={vendorOptions}
              invalid={Boolean(issueFor('vendorId'))}
              describedBy={issueFor('vendorId') ? 'fnd-vendor-issue' : undefined}
              onChange={(id) => set('vendorId', id)}
            />
            {issueFor('vendorId') && (
              <p className="field-issue" id="fnd-vendor-issue">
                {issueFor('vendorId')?.detail}
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="fnd-severity">Severity</label>
            <select id="fnd-severity" value={draft.severity} onChange={(e) => set('severity', e.target.value)}>
              {FINDING_SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="fnd-title">What has to be fixed</label>
            <input
              id="fnd-title"
              type="text"
              value={draft.title}
              placeholder="No signed processing agreement for the payroll feed"
              aria-invalid={issueFor('title') ? true : undefined}
              aria-describedby={issueFor('title') ? 'fnd-title-issue' : undefined}
              onChange={(e) => set('title', e.target.value)}
            />
            {issueFor('title') && (
              <p className="field-issue" id="fnd-title-issue">
                {issueFor('title')?.detail}
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="fnd-detail">Detail</label>
            <textarea id="fnd-detail" rows={3} value={draft.detail} onChange={(e) => set('detail', e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="fnd-citation">Citation</label>
            <input
              id="fnd-citation"
              type="text"
              value={draft.citation}
              placeholder="DPDP Act 2023, section 8(5)"
              onChange={(e) => set('citation', e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="fnd-risk-type">Risk type</label>
            <select id="fnd-risk-type" value={draft.riskType} onChange={(e) => set('riskType', e.target.value)}>
              <option value="">Not tied to one type</option>
              {riskTypes.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="fnd-owner">Owner</label>
            <input
              id="fnd-owner"
              type="text"
              value={draft.ownerUserId}
              placeholder="Leave blank for the vendor's TPRM owner"
              onChange={(e) => set('ownerUserId', e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="fnd-due">Due on</label>
            <input
              id="fnd-due"
              type="date"
              value={draft.dueOn}
              aria-invalid={issueFor('dueOn') ? true : undefined}
              aria-describedby={issueFor('dueOn') ? 'fnd-due-issue' : undefined}
              onChange={(e) => set('dueOn', e.target.value)}
              onBlur={leaveDue}
            />
            {issueFor('dueOn') && (
              <p className="field-issue" id="fnd-due-issue" role="alert">
                {issueFor('dueOn')?.detail}
              </p>
            )}
          </div>

        </form>
      </Dialog>
    </div>
  )
}
